import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { doc, setDoc, collection, Timestamp } from 'firebase/firestore';
import { auth, db } from '../../services/firebase';
import AccountSetup from './AccountSetup';
import DietarySetup from './DietarySetup';
import CareProfilesStep from './CareProfilesStep';

// ── Step constants ────────────────────────────────────────────────────────────
const STEP_ACCOUNT  = 1;
const STEP_DIETARY  = 2;
const STEP_CARE     = 3;
const STEP_SAVING   = 4;

// ── Friendly Firebase error messages (mirrors iOS friendlyError) ──────────────
const friendlyError = (err) => {
  const code = err?.code || '';
  if (code === 'auth/email-already-in-use') return 'This email is already registered. Try signing in instead.';
  if (code === 'auth/invalid-email')        return 'Please enter a valid email address.';
  if (code === 'auth/weak-password')        return 'Password must be at least 8 characters.';
  if (code === 'auth/network-request-failed') return 'Network error. Please check your connection.';
  if (code === 'auth/too-many-requests')    return 'Too many attempts. Please try again later.';
  return 'Something went wrong. Please try again.';
};

const OnboardingFlow = () => {
  const navigate = useNavigate();

  const [step,    setStep]    = useState(STEP_ACCOUNT);
  const [error,   setError]   = useState('');
  const [saving,  setSaving]  = useState(false);

  // ── Collected data (nothing written to Firebase until the end) ────────────
  const [account, setAccount] = useState({
    firstName:       '',
    lastName:        '',
    username:        '',
    email:           '',
    password:        '',
    acceptedTerms:   false,
    acceptedPrivacy: false,
  });

  const [dietary, setDietary] = useState({
    dietStyle:  null,
    allergies:  new Set(),
    avoid:      new Set(),
  });

  const [, setCareProfiles] = useState([]);

  // ── Step handlers ─────────────────────────────────────────────────────────

  const handleAccountContinue = () => {
    setError('');
    setStep(STEP_DIETARY);
  };

  const handleDietaryContinue = (data) => {
    setDietary(data);
    setStep(STEP_CARE);
  };

  const handleDietarySkip = () => {
    setStep(STEP_CARE);
  };

  const handleCareComplete = async (profiles) => {
    setCareProfiles(profiles);
    await saveEverything(profiles);
  };

  const handleCareSkip = async () => {
    await saveEverything([]);
  };

  // ── Single Firebase save (mirrors iOS completeOnboarding) ─────────────────
  const saveEverything = async (profiles) => {
    setError('');
    setSaving(true);
    setStep(STEP_SAVING);

    try {
      // 1. Create Firebase Auth user
      const credential = await createUserWithEmailAndPassword(
        auth, account.email, account.password
      );
      const { user } = credential;

      // 2. Set Firebase display name
      const displayName = `${account.firstName.trim()} ${account.lastName.trim()}`.trim();
      await updateProfile(user, { displayName });

      // 3. Build Firestore user document (mirrors iOS profileData shape)
      const userRef  = doc(db, 'users', user.uid);
      const username = account.username.trim().replace(/^@/, '').toLowerCase();

      await setDoc(userRef, {
        uid:         user.uid,
        email:       user.email,
        displayName,
        name:        displayName,
        firstName:   account.firstName.trim(),
        lastName:    account.lastName.trim(),
        username,
        dietaryProfile: {
          dietStyle:        dietary.dietStyle || null,
          allergies:        [...dietary.allergies],
          avoidIngredients: [...dietary.avoid],
        },
        legal: {
          acceptedTerms:   account.acceptedTerms,
          acceptedPrivacy: account.acceptedPrivacy,
          acceptanceDate:  Timestamp.now(),
        },
        onboardingCompleted: true,
        createdAt:  Timestamp.now(),
        updatedAt:  Timestamp.now(),
        version:    1,
      });

      // 4. Save care profiles as sub-collection (mirrors iOS saveCareProfile) 
      for (const profile of profiles) {
        const ref = doc(collection(db, 'users', user.uid, 'careProfiles'));
        await setDoc(ref, {
          userId:               user.uid,
          name:                 profile.name,
          relationship:         profile.relationship,
          ageGroup:             profile.ageGroup || null,
          tags:                 profile.dietaryTags || [],
          allergies:            (profile.allergies || []).map(a => a.toLowerCase()),
          dietaryRestrictions:  (profile.avoidIngredients || []).map(a => a.toLowerCase()),
          notes:                profile.notes || '',
          isAdmin:              false,
          createdAt:            Timestamp.now(),
          updatedAt:            Timestamp.now(),
        });
      }

      // 5. Done — navigate to home
      navigate('/home', { replace: true });

    } catch (err) {
      console.error('Onboarding save error:', err);
      setError(friendlyError(err));
      setSaving(false);
      setStep(STEP_CARE);
    }
  };

  // ── Saving screen ─────────────────────────────────────────────────────────
  if (step === STEP_SAVING || saving) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#2D5F4D]/8 via-[#8B6F47]/5 to-[#F5F3EF] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-14 w-14 border-b-4 border-[#2D5F4D] mx-auto mb-lg" />
          <p className="text-callout font-semibold text-[#2D5F4D]">Setting up your account…</p>
          <p className="text-caption text-[#8B7355] mt-xs">This only takes a moment</p>
        </div>
      </div>
    );
  }

  // ── Progress indicator ────────────────────────────────────────────────────
  const totalSteps   = 3;
  const currentStep  = Math.min(step, totalSteps);

  return (
    <div className="relative">
      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 z-50 h-1 bg-[#2D5F4D]/10">
        <div
          className="h-full bg-[#2D5F4D] transition-all duration-500"
          style={{ width: `${(currentStep / totalSteps) * 100}%` }}
        />
      </div>

      {/* Error banner */}
      {error && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-red-50 border border-red-200 text-red-700 text-callout font-medium px-lg py-md rounded-2xl shadow-lg max-w-md w-full mx-auto">
          {error}
        </div>
      )}

      {/* Steps */}
      {step === STEP_ACCOUNT && (
        <AccountSetup
          data={account}
          onChange={setAccount}
          onContinue={handleAccountContinue}
        />
      )}

      {step === STEP_DIETARY && (
        <DietarySetup
          data={dietary}
          onChange={setDietary}
          onContinue={handleDietaryContinue}
          onSkip={handleDietarySkip}
        />
      )}

      {step === STEP_CARE && (
        <CareProfilesStep
          onComplete={handleCareComplete}
          onSkip={handleCareSkip}
        />
      )}
    </div>
  );
};

export default OnboardingFlow;